import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  Promotion,
  PromotionDraft,
  useActivatePromotion,
  useCreatePromotion,
  useDeactivatePromotion,
  useDeletePromotion,
  usePromotions,
  useUpdatePromotion,
} from '../../queries/promotions';
import { PromotionFormDialog } from './PromotionFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const EMPTY_DRAFT: PromotionDraft = {};

export function PromotionsPage() {
  const { data: promotions = [], isLoading } = usePromotions();
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const remove = useDeletePromotion();
  const activate = useActivatePromotion();
  const deactivate = useDeactivatePromotion();
  const { success, error } = useToast();

  const [editing, setEditing] = useState<PromotionDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Promotion | null>(null);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!editing) return;
    setFormError(null);
    const isEdit = !!editing.id;
    const onSettled = {
      onSuccess: () => {
        setEditing(null);
        success(isEdit ? 'Promotion updated.' : 'Promotion created.');
      },
      onError: (err: unknown) => setFormError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.title}".`);
        setDeleting(null);
      },
      onError: (err: unknown) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const handleActivate = (promotion: Promotion) => {
    activate.mutate(promotion.id, {
      onSuccess: () => success(`"${promotion.title}" is now the active promotion.`),
      onError: (err: unknown) => error(extractErrorMessage(err, 'Activate failed.')),
    });
  };

  const handleDeactivate = (promotion: Promotion) => {
    deactivate.mutate(promotion.id, {
      onSuccess: () => success(`Deactivated "${promotion.title}".`),
      onError: (err: unknown) => error(extractErrorMessage(err, 'Deactivate failed.')),
    });
  };

  const columns: DataTableColumn<Promotion>[] = [
    { key: 'title', header: 'Title', priority: true, render: (p) => <span className="font-medium">{p.title}</span> },
    { key: 'source', header: 'Source', priority: false, render: (p) => <span className="font-mono text-xs text-muted-foreground">{p.source}</span> },
    { key: 'code', header: 'Discount code', priority: false, render: (p) => <span className="font-mono text-xs text-muted-foreground">{p.discount_code ?? '—'}</span> },
    { key: 'active', header: 'Active', priority: true, render: (p) => (p.is_active ? <Badge variant="success">Active</Badge> : <span className="text-muted-foreground">—</span>) },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (p) => (
        <div className="flex flex-wrap items-center justify-end gap-1">
          {p.is_active ? (
            <Button variant="outline" size="sm" onClick={() => handleDeactivate(p)} disabled={deactivate.isPending}>
              Deactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => handleActivate(p)} disabled={activate.isPending}>
              Activate
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              setEditing({
                id: p.id,
                source: p.source,
                discount_code: p.discount_code,
                title: p.title,
                description: p.description,
                image_url: p.image_url,
                package_code: p.package_code,
              })
            }
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleting(p)} className="text-muted-foreground hover:text-destructive" title="Delete">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Promotions"
        description="Only one promotion can be active at a time."
        actions={
          <Button onClick={() => setEditing({ ...EMPTY_DRAFT })}>
            <Plus className="h-4 w-4" />
            Add promotion
          </Button>
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={promotions} getRowKey={(p) => String(p.id)} emptyMessage="No promotions yet." />
      )}

      {editing && (
        <PromotionFormDialog
          draft={editing}
          onChange={setEditing}
          onCancel={() => {
            setEditing(null);
            setFormError(null);
          }}
          onSave={handleSave}
          saving={saving}
          error={formError}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete promotion?"
        description={`"${deleting?.title}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
